import React from "react";
import {
  messageTypeToComponentMap,
  TessaResponseMessages
} from "@webV2/pages/Tessa/types";
import {
  AiChatTextPart,
  TessaChatMessage,
  TessaMessageTypes
} from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { useAuthenticated } from "@gettactic/hooks";
import { UserAvatar } from "@v2/components/display/UserAvatar/UserAvatar";
import { parseMessageContent } from "@webV2/pages/Tessa/utils/parseContent";

export interface TessaChatMessageProps {
  message: TessaChatMessage | TessaResponseMessages;
  reversedParts?: boolean;
  showUserAvatar?: boolean;
}

export const TessaChatMessageBubble: React.FC<TessaChatMessageProps> = ({
  message,
  reversedParts = true,
  showUserAvatar = false
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const user = authenticatedUser?.user;
  const messageRole = message.role;
  const isResponse = messageRole === "assistant" || messageRole === "tool";
  const parts = React.useMemo(() => {
    if (message.role === "tool" && message.tool_name && message.content) {
      const toolPart = {
        type: `tool-${message.tool_name}`,
        output: message.content
      };

      return [toolPart];
    }
    if ("parts" in message) {
      return message.parts;
    }
    return parseMessageContent(message.content);
  }, [message]);

  const displayParts = React.useMemo(() => {
    return (reversedParts ? [...parts].reverse() : parts).filter((part) => {
      if (part.type === TessaMessageTypes.text) {
        const textPart = part as AiChatTextPart;
        return textPart.text && textPart.text.trim().length > 0;
      }
      return true;
    });
  }, [parts, reversedParts]);

  return (
    <div
      className={cn(
        "mx-auto flex w-full max-w-[1200px]",
        isResponse ? "" : "justify-end"
      )}
    >
      <div
        className={cn(
          isResponse ? "" : "rounded-2xl bg-neutral-50 px-4 py-0",
          "motion-safe:fade-in-0 motion-safe:zoom-in-125 motion-safe:animate-in motion-safe:fill-mode-both motion-safe:duration-300 motion-safe:ease-out"
        )}
      >
        {displayParts?.map((part, partIndex) => {
          const MessageComponent =
            messageTypeToComponentMap[
              part.type as keyof typeof messageTypeToComponentMap
            ];
          if (!MessageComponent) return null;

          return (
            <MessageComponent
              key={partIndex}
              part={part as never}
              isResponse={isResponse}
              message={message}
            />
          );
        })}
      </div>
      {showUserAvatar && !isResponse && user && (
        <div className={"-mt-1 absolute"}>
          <UserAvatar
            size="xs"
            user={{
              name: user.name,
              profile_photo_url: user.profile_photo_url
            }}
          />
        </div>
      )}
    </div>
  );
};
